import { Dispatch, MouseEvent, ReactNode, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Localized, useLocalization } from "@fluent/react"
import { Alert, alpha, Badge, Box, Button, Checkbox, Chip, Dialog, FormControlLabel, FormGroup, IconButton, InputAdornment, Link, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Skeleton, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Tooltip, Typography } from "@mui/material"
import { blue, teal } from "@mui/material/colors"
import AddIcon from "@mui/icons-material/Add"
import JoinInnerIcon from "@mui/icons-material/JoinInner"
import DeleteForeverIcon from "@mui/icons-material/DeleteForever"
import NoteAddIcon from "@mui/icons-material/NoteAdd"
import LinkIcon from "@mui/icons-material/Link"
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined"
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined"
import SearchIcon from "@mui/icons-material/Search"
import CloseIcon from "@mui/icons-material/Close"
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import PrintIcon from "@mui/icons-material/Print"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import { AppId, AppTextFieldHandler } from "../../app/appTypes"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { AppLink } from "../../app/AppLink"
import { LoadRequirements, LoadRequirementsTypes, LoadRequirementTestMappings, LoadStructure } from "../../app/AppDataLoader"
import { StructureName } from "../device/Structure"
import { selectStructure, selectStructureNodeById } from "../device/structureSlice"
import { SimpleStructureComponent } from "../structure/SimpleStructureComponent"
import { selectSettings } from "../settings/settingsSlice"
import { Requirement, selectRequirementsEntities, selectRequirementsForStructureNode } from "./RequirementsDictSlice"
import { selectAllRequirementsTypes } from "./RequirementsTypesSlice"
import { selectTestIdByRequirementId } from "./requirementTestSlice"
import { selectModule } from "../modules/moduleSlice"
import { showError, showSuccess } from "../notifications/notificationsSlice"
import { CreateUpdateRequirement } from "./dialogs/CreateUpdateRequirement"
import { DeleteRequirement } from "./dialogs/DeleteRequirement"
import { AssociateTest } from "./dialogs/AssociateTest"
import { DeleteRequirementTest } from "./dialogs/DeleteRequirementTest"
import { toolbarMinHeight } from "../.."
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import { UserNote, UserNotesProvider, useUserNotes } from "../notes/UserNotesContext"
import { UserNotesPanel } from "../notes/UserNotesPanel"
import { UserNotesButton } from "../notes/UserNotesButton"

const REQUIREMENTS_IN_STRUCTURE_SCREEN_GUID = "de38315b-6469-4c8f-a948-de3c876d5ac4"

export const RequirementsInStructure = () => <UserNotesProvider screenGuid={REQUIREMENTS_IN_STRUCTURE_SCREEN_GUID}>
    <RequirementsInStructureContent />
</UserNotesProvider>

const RequirementsInStructureContent = () => {
    const { l10n } = useLocalization()
    const [structureId, setStructureId] = useState<AppId | null>(null)
    const [structureCollapsed, setStructureCollapsed] = useState(false)
    const [focusedRequirementId, setFocusedRequirementId] = useState<AppId | null>(null)
    const requirementsById = useAppSelector(selectRequirementsEntities)
    const structuresById = useAppSelector(state => selectStructure(state).entities)
    const { noteCount, openNotes } = useUserNotes()
    const dispatch = useAppDispatch()
    const navigateToNote = (note: UserNote) => {
        if (note.objectType === "STRUCTURE" && note.objectGuid && structuresById[note.objectGuid]) {
            setStructureId(note.objectGuid)
            setFocusedRequirementId(null)
            setStructureCollapsed(false)
            return
        }
        const requirement = note.objectType === "REQUIREMENT" && note.objectGuid
            ? requirementsById[note.objectGuid]
            : undefined
        if (!requirement) {
            dispatch(showError("user-notes-object-unavailable"))
            return
        }
        setStructureId(requirement.structureId)
        setFocusedRequirementId(requirement.id)
        setStructureCollapsed(false)
    }
    const collapseStructureLabel = l10n.getString("requirements-in-structure-collapse-panel", null, "_Collapse structure panel")
    const showStructureButton = structureCollapsed && <Button
        size="small"
        variant="outlined"
        startIcon={<ChevronRightIcon />}
        onClick={() => setStructureCollapsed(false)}
        aria-expanded={false}
        aria-controls="requirements-structure-tree"
        sx={{ flexShrink: 0, alignSelf: 'flex-start' }}
    >
        <Localized id="requirements-in-structure-expand-panel">_Expand structure panel</Localized>
    </Button>

    return <LoadStructure component={<Skeleton variant="rectangular" height="70vh" />}>
        <>
        <Box sx={theme => ({
            display: 'grid',
            gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                lg: structureCollapsed ? 'minmax(0, 1fr)' : 'minmax(280px, 25%) minmax(0, 1fr)',
            },
            gap: 2,
            height: { xs: 'auto', lg: `calc(100dvh - ${toolbarMinHeight}px - ${theme.spacing(6)})` },
            minHeight: 400,
        })}>
            <Paper variant="outlined" sx={{
                p: 2,
                minWidth: 0,
                minHeight: 0,
                display: structureCollapsed ? 'none' : 'flex',
                flexDirection: 'column',
                height: { xs: 360, lg: 'auto' },
            }}>
                <Stack direction="row" alignItems="center" sx={{ mb: 1, flexShrink: 0 }}>
                    <Typography variant="h6" sx={{ flex: 1 }}>
                        <Localized id="menu-structure">_Structure</Localized>
                    </Typography>
                    <Tooltip title={collapseStructureLabel}>
                        <IconButton
                            size="small"
                            onClick={() => setStructureCollapsed(true)}
                            aria-label={collapseStructureLabel}
                            aria-expanded={true}
                            aria-controls="requirements-structure-tree"
                        >
                            <ChevronLeftIcon />
                        </IconButton>
                    </Tooltip>
                </Stack>
                <Box id="requirements-structure-tree" sx={{ flex: 1, minHeight: 0 }}>
                    <SimpleStructureComponent expandedOnOpen={false} selectedStructureId={structureId}
                        onNodeSelected={id => {
                            setStructureId(id)
                            setFocusedRequirementId(null)
                        }} />
                </Box>
            </Paper>
            <Paper variant="outlined" sx={{
                p: 2,
                minWidth: 0,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                height: { xs: '70vh', lg: 'auto' },
            }}>
                <Paper variant="outlined" component="section"
                    sx={{ mb: 1, px: 1, py: 0.5, flexShrink: 0, bgcolor: 'action.hover' }}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant="subtitle2" sx={{ flex: 1 }}>
                            <Localized id="requirements-in-structure-tools">_Screen tools</Localized>
                        </Typography>
                        <Tooltip title={l10n.getString("user-notes-all", null, "_All notes")}>
                            <IconButton size="small" onClick={() => openNotes(null)}
                                aria-label={l10n.getString("user-notes-all", null, "_All notes")}>
                                <Badge badgeContent={noteCount} color="warning">
                                    <NoteAddIcon fontSize="small" />
                                </Badge>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={l10n.getString("requirements-in-structure-pdf-coming-soon", null, "_PDF printout (coming soon)")}>
                            <span>
                                <IconButton size="small" disabled
                                    aria-label={l10n.getString("requirements-in-structure-pdf-coming-soon", null, "_PDF printout (coming soon)")}>
                                    <PrintIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                </Paper>
                {structureId === null
                    ? <Stack spacing={2}>
                        {showStructureButton}
                        <Alert severity="info">
                            <Localized id="requirements-in-structure-select">_Select a structure on the left to view its requirements.</Localized>
                        </Alert>
                    </Stack>
                    : <>
                        <LoadRequirementsTypes component={<Skeleton height={120} />}>
                            <LoadRequirements component={<Skeleton height={120} />}>
                                <LoadRequirementTestMappings component={<Skeleton height={120} />}>
                                    <StructureRequirementsList
                                        key={structureId}
                                        structureId={structureId}
                                        headerAction={showStructureButton}
                                        focusedRequirementId={focusedRequirementId}
                                    />
                                </LoadRequirementTestMappings>
                            </LoadRequirements>
                        </LoadRequirementsTypes>
                    </>}
            </Paper>
        </Box>
        <UserNotesPanel onNavigate={navigateToNote} />
        </>
    </LoadStructure>
}

type DialogState =
    | { type: "none" }
    | { type: "delete", requirementId: AppId }
    | { type: "createUpdate", requirementId: AppId | undefined, requirementTypeId: AppId | undefined }
    | { type: "assocTest", requirementId: AppId }
    | { type: "deleteAssoc", requirementId: AppId }

const RequirementDialogContent = ({ state, setter, structureId }: {
    state: DialogState
    setter: Dispatch<SetStateAction<DialogState>>
    structureId: AppId
}) => {
    const dispatch = useAppDispatch()
    const onSuccess = useCallback(() => {
        setter({ type: "none" })
        dispatch(showSuccess("saved"))
    }, [setter, dispatch])
    const onCancel = useCallback(() => {
        setter({ type: "none" })
    }, [setter])
    const onError = useCallback(() => {
        setter({ type: "none" })
        dispatch(showError("error"))
    }, [setter, dispatch])

    switch (state.type) {
        case "none":
            return null
        case "delete":
            return <DeleteRequirement
                requirementId={state.requirementId}
                onSuccess={onSuccess}
                onCancel={onCancel}
                onError={onError}
            />
        case "createUpdate":
            return <CreateUpdateRequirement
                id={state.requirementId}
                defaultDeviceTypeId={structureId}
                useGlobalStructureContext={false}
                defaultRequirementTypeId={state.requirementTypeId ?? ""}
                onSaveSuccess={onSuccess}
                onCancel={onCancel}
            />
        case "assocTest":
            return <AssociateTest
                requirementId={state.requirementId}
                onCancel={onCancel}
                onSuccess={onSuccess}
                onError={onError}
            />
        case "deleteAssoc":
            return <DeleteRequirementTest
                requirementId={state.requirementId}
                onCancel={onCancel}
                onSuccess={onSuccess}
                onError={onError}
            />
    }
}

const StructureRequirementsList = ({ structureId, headerAction, focusedRequirementId }: {
    structureId: AppId
    headerAction?: ReactNode
    focusedRequirementId: AppId | null
}) => {
    const { l10n } = useLocalization()
    const selectedStructure = useAppSelector(state => selectStructureNodeById(state, structureId))
    const { locale } = useAppSelector(selectSettings)
    const hasChildren = (selectedStructure?.subStructureIds.length ?? 0) > 0
    const currentModule = useAppSelector(state => selectModule(state).currentModule)
    const isSuper = currentModule?.code === "super"
    const requirementTypes = useAppSelector(selectAllRequirementsTypes)
    const [typeId, setTypeId] = useState<AppId | undefined>(requirementTypes[0]?.id)
    const [showInherited, setShowInherited] = useState(true)
    const [showSystem, setShowSystem] = useState(true)
    const [search, setSearch] = useState("")
    const [dialog, setDialog] = useState<DialogState>({ type: "none" })
    const allRequirements = useAppSelector(state => selectRequirementsForStructureNode(state, structureId))
    const focusedRow = useRef<HTMLTableRowElement>(null)
    useEffect(() => {
        if (!focusedRequirementId) return
        const requirement = allRequirements.find(item => item.id === focusedRequirementId)
        if (!requirement) return
        setTypeId(requirement.typeId)
        setShowInherited(true)
        setShowSystem(true)
        setSearch("")
    }, [focusedRequirementId, allRequirements])
    // Liczniki dla całej wybranej kategorii, niezależnie od filtrów widoczności.
    const requirementCounts = useMemo(() => {
        const categoryRequirements = allRequirements.filter(requirement => requirement.typeId === typeId)
        const own = categoryRequirements.filter(requirement => requirement.structureId === structureId).length

        return {
            own,
            inherited: categoryRequirements.length - own,
            total: categoryRequirements.length,
        }
    }, [allRequirements, typeId, structureId])

    const searchableRequirements = useMemo(() => {
        const parser = new DOMParser()
        return allRequirements.map(requirement => ({
            requirement,
            text: [
                requirement.code,
                ...[requirement.name, requirement.name_EN, requirement.description].map(html => {
                    const body = parser.parseFromString(html ?? "", 'text/html').body
                    body.querySelectorAll('br, p, div, li, td, th').forEach(element => element.append(' '))
                    return body.textContent ?? ""
                }),
            ].join(' ').replace(/\s+/g, ' ').toLowerCase(),
        }))
    }, [allRequirements])
    const searchText = search.trim().replace(/\s+/g, ' ').toLowerCase()
    const requirements = useMemo(
        () => searchableRequirements.filter(({ requirement, text }) =>
            requirement.typeId === typeId
            && (showInherited || requirement.structureId === structureId)
            && (showSystem || !requirement.isSystem)
            && text.includes(searchText)
        ).map(({ requirement }) => requirement),
        [searchableRequirements, typeId, structureId, showInherited, showSystem, searchText]
    )
    useEffect(() => {
        if (focusedRequirementId && requirements.some(item => item.id === focusedRequirementId)) {
            focusedRow.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
    }, [focusedRequirementId, requirements])
    const selectedType = requirementTypes.find(type => type.id === typeId)

    const handleTypeChange = useCallback((_, newTypeId: AppId) => {
        setTypeId(newTypeId)
    }, [])
    const handleSearchChange: AppTextFieldHandler = useCallback(event => {
        setSearch(event.target.value)
    }, [])
    const handleClearSearch = useCallback(() => {
        setSearch("")
    }, [])
    const handleAdd = useCallback(() => {
        setDialog({ type: "createUpdate", requirementId: undefined, requirementTypeId: typeId })
    }, [typeId])
    const handleEdit = useCallback((requirementId: AppId) => {
        setDialog({ type: "createUpdate", requirementId, requirementTypeId: typeId })
    }, [typeId])
    const handleAssociateTest = useCallback((requirementId: AppId) => {
        setDialog({ type: "assocTest", requirementId })
    }, [])
    const handleDeleteAssociation = useCallback((requirementId: AppId) => {
        setDialog({ type: "deleteAssoc", requirementId })
    }, [])

    return <>
        <Stack
            direction="row"
            alignItems="center"
            sx={theme => ({
                gap: 1.25,
                flexWrap: 'wrap',
                mb: 1,
                px: 1.5,
                py: 1,
                flexShrink: 0,
                borderRadius: 1,
                borderLeft: 3,
                borderColor: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.06),
            })}
        >
            {hasChildren ? (
                <FolderOutlinedIcon sx={{ color: 'primary.main', fontSize: 22, flexShrink: 0 }} />
            ) : (
                <SettingsOutlinedIcon sx={{ color: 'text.secondary', fontSize: 22, flexShrink: 0 }} />
            )}
            <Box sx={{ flex: '1 1 180px', minWidth: 0 }}>
                <Stack direction="row" alignItems="baseline" sx={{ gap: 1, flexWrap: 'wrap' }}>
                    <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700, overflowWrap: 'anywhere', minWidth: 0 }}>
                        <StructureName structureId={structureId} />
                    </Typography>
                    {selectedStructure && hasChildren && (
                        <Typography variant="caption" color="text.secondary">
                            <Localized id="requirements-in-structure-children">Elementy podrzędne</Localized>
                            {`: ${selectedStructure.subStructureIds.length}`}
                        </Typography>
                    )}
                </Stack>
                <Stack
                    direction="row"
                    alignItems="center"
                    aria-live="polite"
                    sx={{ columnGap: 1.5, rowGap: 0.5, flexWrap: 'wrap' }}
                >
                    {selectedType && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            {selectedType.code}
                        </Typography>
                    )}
                    <Typography
                        component="span"
                        variant="caption"
                        sx={theme => ({
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            whiteSpace: 'nowrap',
                            color: blue[theme.palette.mode === 'dark' ? 300 : 700],
                        })}
                    >
                        <PushPinOutlinedIcon sx={{ fontSize: 15 }} />
                        <Box component="span">
                            <Localized id="requirements-in-structure-count-own">Własne</Localized>
                            {':'}
                        </Box>
                        <Box component="span" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                            {requirementCounts.own}
                        </Box>
                    </Typography>
                    <Typography
                        component="span"
                        variant="caption"
                        sx={theme => ({
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            whiteSpace: 'nowrap',
                            color: teal[theme.palette.mode === 'dark' ? 300 : 700],
                        })}
                    >
                        <AccountTreeOutlinedIcon sx={{ fontSize: 15 }} />
                        <Box component="span">
                            <Localized id="requirements-in-structure-count-inherited">Dziedziczone</Localized>
                            {':'}
                        </Box>
                        <Box component="span" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                            {requirementCounts.inherited}
                        </Box>
                    </Typography>
                </Stack>
            </Box>
            {selectedStructure && <UserNotesButton
                target={{
                    objectType: "STRUCTURE",
                    objectGuid: structureId,
                    label: locale === "pl" ? selectedStructure.name : selectedStructure.name_EN || selectedStructure.name,
                }}
                title={l10n.getString("user-notes-for-structure", null, "_Notes for this structure")}
            />}
            {headerAction && <Box sx={{ ml: 'auto', flexShrink: 0 }}>{headerAction}</Box>}
        </Stack>
        <Paper
            component="section"
            variant="outlined"
            aria-labelledby="requirements-filters-title"
            sx={{ mb: 1, p: 1, flexShrink: 0, bgcolor: 'action.hover', borderRadius: 1 }}
        >
            <Stack direction="row" alignItems="center" sx={{ gap: 1, flexWrap: 'wrap' }}>
                <Stack direction="row" spacing={0.5} alignItems="center">
                    <FilterAltOutlinedIcon color="primary" fontSize="small" />
                    <Typography id="requirements-filters-title" component="h3" variant="subtitle2" fontWeight={700}>
                        <Localized id="requirements-in-structure-filters">_Filters</Localized>
                    </Typography>
                </Stack>
                <TextField
                    size="small"
                    value={search}
                    onChange={handleSearchChange}
                    label={<Localized id="requirements-in-structure-search">_Search requirements</Localized>}
                    sx={{ flex: '1 1 200px', minWidth: 180, '& .MuiInputBase-root': { bgcolor: 'background.paper' } }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                        endAdornment: <InputAdornment position="end">
                            <IconButton
                                size="small"
                                onClick={handleClearSearch}
                                disabled={search === ""}
                                aria-label={l10n.getString("clear", null, "_Clear")}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </InputAdornment>,
                    }}
                />
                <FormGroup row sx={{ gap: 0.5 }}>
                    <FormControlLabel
                        sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                        control={<Checkbox size="small" sx={{ p: 0.5 }} checked={showInherited} onChange={(_, checked) => {
                            setShowInherited(checked)
                        }} />}
                        label={<Localized id="requirements-in-structure-show-inherited">_Show inherited</Localized>}
                    />
                    <FormControlLabel
                        sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                        control={<Checkbox size="small" sx={{ p: 0.5 }} checked={showSystem} onChange={(_, checked) => {
                            setShowSystem(checked)
                        }} />}
                        label={<Localized id="requirements-in-structure-show-system">_Show system requirements</Localized>}
                    />
                </FormGroup>
            </Stack>
        </Paper>
        <Box sx={{ minHeight: 0, minWidth: 0, flexGrow: 1, bgcolor: 'background.paper', display: 'flex' }}>
            <Tabs
                orientation="vertical"
                variant="scrollable"
                value={typeId ?? false}
                onChange={handleTypeChange}
                sx={{ borderRight: 1, borderColor: 'divider' }}
            >
                {requirementTypes.map(type => <Tab key={type.id} value={type.id} label={type.code} />)}
            </Tabs>
            <Stack spacing={2} sx={{ width: '100%', minWidth: 0, minHeight: 0, pl: 1, pt: 1, bgcolor: 'action.hover' }}>
                <Stack direction="row" alignItems="center" sx={{ gap: 1, flexWrap: 'wrap', flexShrink: 0 }}>
                    <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
                        {selectedType && `${selectedType.code} - ${selectedType.name}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" aria-live="polite" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                        <Localized id="requirements-in-structure-count-visible">Widoczne</Localized>
                        {`: ${requirements.length} / ${requirementCounts.total}`}
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleAdd}
                        disabled={!selectedType}
                        disableElevation
                        sx={{
                            minHeight: 34,
                            px: 1.5,
                            borderRadius: 1,
                            fontWeight: 600,
                            textTransform: 'none',
                            flexShrink: 0,
                        }}
                    >
                        <Localized
                            id="requirements-in-structure-add-to-type"
                            vars={{ type: selectedType?.code ?? '' }}
                        >
                            <span>{selectedType ? `Dodaj do ${selectedType.code}` : 'Dodaj wymaganie'}</span>
                        </Localized>
                    </Button>
                </Stack>
                <TableContainer component={Paper} sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ minWidth: 120, width: 120, maxWidth: 120 }}>
                                    <Localized id="requirements-code">_Code</Localized>
                                </TableCell>
                                <TableCell><Localized id="requirements-name">_Name</Localized></TableCell>
                                <TableCell><Localized id="requirements-in-structure-applicability">_Scope of applicability</Localized></TableCell>
                                <TableCell><Localized id="system-item">_System</Localized></TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {requirements.length === 0 && <TableRow>
                                <TableCell colSpan={5}>
                                    <Localized id="requirements-in-structure-empty">_No requirements match the selected structure, type and filters.</Localized>
                                </TableCell>
                            </TableRow>}
                            {requirements.map(requirement => {
                                const isDirect = requirement.structureId === structureId
                                return <TableRow key={requirement.id}
                                    ref={requirement.id === focusedRequirementId ? focusedRow : undefined}
                                    sx={requirement.id === focusedRequirementId ? { bgcolor: 'action.selected' } : undefined}>
                                    <TableCell>{requirement.code}</TableCell>
                                    <TableCell><div dangerouslySetInnerHTML={{ __html: requirement.name }} /></TableCell>
                                    <TableCell>
                                        <Stack spacing={0.75} alignItems="flex-start">
                                            <Chip
                                                size="small"
                                                icon={isDirect ? <PushPinOutlinedIcon /> : <AccountTreeOutlinedIcon />}
                                                label={isDirect
                                                    ? <Localized id="requirements-in-structure-direct">_Only this element</Localized>
                                                    : <Localized id="requirements-in-structure-shared">_Shared by the group</Localized>}
                                                sx={theme => {
                                                    const color = (isDirect ? blue : teal)[theme.palette.mode === 'dark' ? 300 : 700]
                                                    return {
                                                        color,
                                                        bgcolor: alpha(color, 0.1),
                                                        borderRadius: 1,
                                                        height: 32,
                                                        fontSize: '0.875rem',
                                                        '& .MuiChip-icon': { color: 'inherit' },
                                                    }
                                                }}
                                            />
                                            <Typography variant="body2" color="text.secondary">
                                                <StructureName structureId={requirement.structureId} />
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell><Checkbox checked={requirement.isSystem} disabled /></TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                        <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.5}>
                                            <RequirementTestMenu
                                                requirement={requirement}
                                                isSuper={isSuper}
                                                associateTest={handleAssociateTest}
                                                deleteAssociation={handleDeleteAssociation}
                                            />
                                            <Tooltip title={l10n.getString("edit", null, "_Edit")}>
                                                <span>
                                                    <IconButton size="small" color="secondary"
                                                        onClick={() => handleEdit(requirement.id)}
                                                        disabled={!isSuper && requirement.isSystem}
                                                        aria-label={l10n.getString("edit", null, "_Edit")}>
                                                        <EditOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title={l10n.getString("delete", null, "_Delete")}>
                                                <span>
                                                    <IconButton size="small" color="primary"
                                                        onClick={() => setDialog({ type: "delete", requirementId: requirement.id })}
                                                        disabled={!isSuper && requirement.isSystem}
                                                        aria-label={l10n.getString("delete", null, "_Delete")}>
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <UserNotesButton
                                                target={{
                                                    objectType: "REQUIREMENT",
                                                    objectGuid: requirement.id,
                                                    label: requirement.code,
                                                }}
                                                title={l10n.getString("user-notes-for-requirement", null, "_Notes for this requirement")}
                                            />
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Stack>
        </Box>
        <Dialog open={dialog.type !== "none"} fullWidth maxWidth="md" keepMounted={false}>
            <RequirementDialogContent state={dialog} setter={setDialog} structureId={structureId} />
        </Dialog>
    </>
}

const RequirementTestMenu = ({ requirement, isSuper, associateTest, deleteAssociation }: {
    requirement: Requirement
    isSuper: boolean
    associateTest: (requirementId: AppId) => void
    deleteAssociation: (requirementId: AppId) => void
}) => {
    const mapping = useAppSelector(state => selectTestIdByRequirementId(state, requirement.id))
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const handleClose = useCallback(() => setAnchorEl(null), [])
    const handleClick = useCallback((event: MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget), [])
    const handleAssociate = useCallback(() => {
        associateTest(requirement.id)
        setAnchorEl(null)
    }, [associateTest, requirement.id])
    const handleDelete = useCallback(() => {
        if (mapping) {
            deleteAssociation(requirement.id)
            setAnchorEl(null)
        }
    }, [deleteAssociation, requirement.id, mapping])

    if (!isSuper) return null

    return <Box>
        <Button color="secondary" variant={mapping ? "contained" : "outlined"} onClick={handleClick}>
            <Localized id="requirements-test-menu">_Test</Localized>
        </Button>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
            {!mapping && <MenuItem>
                <ListItemIcon><NoteAddIcon fontSize="small" /></ListItemIcon>
                <ListItemText>
                    <Link component={AppLink} to={{
                        pathname: "/sys/validation/new",
                        search: `?requirementId=${requirement.id}`,
                        state: { defaultName: requirement.name },
                    }}>
                        <Localized id="stage-add-new-test">_Add new test</Localized>
                    </Link>
                </ListItemText>
            </MenuItem>}
            {!mapping && <MenuItem onClick={handleAssociate}>
                <ListItemIcon><JoinInnerIcon fontSize="small" /></ListItemIcon>
                <ListItemText><Localized id="requirements-test-associate">_Associate with a test</Localized></ListItemText>
            </MenuItem>}
            {mapping && <MenuItem onClick={handleDelete}>
                <ListItemIcon><DeleteForeverIcon fontSize="small" color="error" /></ListItemIcon>
                <ListItemText><Localized id="requirements-test-unlink">_Remove association</Localized></ListItemText>
            </MenuItem>}
            {mapping && <MenuItem>
                <ListItemIcon><LinkIcon fontSize="small" /></ListItemIcon>
                <ListItemText>
                    <Link component={AppLink} to={{ pathname: `/sys/validation/edit/${mapping.testId}` }}>
                        <Localized id="requirements-test-open">_Go to test</Localized>
                    </Link>
                </ListItemText>
            </MenuItem>}
        </Menu>
    </Box>
}
